import React, { useEffect, useState, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  CircularProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Typography,
  Avatar,
  Chip,
  TextField,
  InputAdornment,
  IconButton,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import { useBreadcrumb } from '../../../contexts/BreadcrumbContext';
import { useSnackbar } from 'notistack';
import api from '../../../services/api';
import { searchAdminProducts } from '../../../services/admin-service';
import { Product as BaseProduct } from '../../../types';

// Extend the base Product interface with additional admin fields
interface AdminProduct extends BaseProduct {
  price_with_tax: number;
  cisp1_commission: number;
  reseller_commission: number;
}

interface ApiResponse {
  items: AdminProduct[];
  total: number;
}

const AdminProductsPage: React.FC = () => {
  const { setBreadcrumbs, setBreadcrumbActions } = useBreadcrumb();
  const { enqueueSnackbar } = useSnackbar();
  const [products, setProducts] = useState<AdminProduct[]>([]);
  const [loading, setLoading] = useState(true);
  const [totalItems, setTotalItems] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState('');
  const [isSearching, setIsSearching] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    setBreadcrumbs([
      { label: 'Painel', path: '/admin' },
      { label: 'Produtos', path: '/admin/products' },
    ]);

    // Add the button action
    setBreadcrumbActions(
      <Button
        variant="contained"
        startIcon={<AddIcon />}
        onClick={() => navigate('/admin/products/new')}
        sx={{
          backgroundColor: '#83b81a',
          color: 'black',
          '&:hover': {
            backgroundColor: '#5b8012',
          },
        }}
      >
        Adicionar Produto
      </Button>
    );

    return () => {
      setBreadcrumbActions(null);
    };
  }, [setBreadcrumbs, setBreadcrumbActions, navigate]);

  const fetchProducts = useCallback(async () => {
    setLoading(true);
    try {
      let data: any;

      if (searchTerm.trim()) {
        // Use search API
        data = await searchAdminProducts(
          searchTerm.trim(),
          page * rowsPerPage,
          rowsPerPage,
          'is_active:desc'
        );
      } else {
        // Direct API call to match your backend format and sort by is_active first
        const url = `/admin/products/?skip=${page * rowsPerPage}&limit=${rowsPerPage}&sort=is_active:desc`;
        console.log('Fetching products from URL:', url);
        data = await api.get<ApiResponse>(url);
      }

      console.log('API response data:', data);

      // Check if data is valid and contains items array
      if (data && Array.isArray(data.items)) {
        console.log('Products array length:', data.items.length);
        setProducts(data.items);
        setTotalItems(data.total || data.items.length);
      } else {
        console.error('Invalid data format or missing items array:', data);
        setProducts([]);
        setTotalItems(0);
      }
    } catch (error) {
      console.error('Error fetching products:', error);
      enqueueSnackbar('Falha ao carregar produtos', { variant: 'error' });
      setProducts([]);
      setTotalItems(0);
    } finally {
      setLoading(false);
    }
  }, [page, rowsPerPage, searchTerm, enqueueSnackbar]);

  useEffect(() => {
    fetchProducts();
  }, [fetchProducts]);

  const handleChangePage = (_event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleSearch = (value: string) => {
    setSearchTerm(value);
    setPage(0); // Reset to first page when searching
  };

  const handleClearSearch = () => {
    setSearchTerm('');
    setPage(0);
  };

  // Format price as currency
  const formatPrice = (price: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
    }).format(price);
  };

  // Calculate final price using the formula
  const calculateFinalPrice = (
    price: number,
    cisp1Commission: number,
    resellerCommission: number
  ) => {
    const TAX_RATIO = 0.09085; // Fixed tax ratio as requested
    const denominator = 1 - TAX_RATIO - cisp1Commission - resellerCommission;

    // Avoid division by zero or negative denominators
    if (denominator <= 0) {
      return null;
    }

    return price / denominator;
  };

  return (
    <>
      {/* Search Bar - Always visible */}
      <Box sx={{ mb: 3, display: 'flex', gap: 2, alignItems: 'center' }}>
        <TextField
          size="small"
          placeholder="Buscar produtos por nome, SKU ou descrição..."
          value={searchTerm}
          onChange={e => handleSearch(e.target.value)}
          sx={{ flexGrow: 1 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon color="action" />
              </InputAdornment>
            ),
            endAdornment: searchTerm && (
              <InputAdornment position="end">
                <IconButton size="small" onClick={handleClearSearch} edge="end">
                  <ClearIcon />
                </IconButton>
              </InputAdornment>
            ),
          }}
        />
        {searchTerm && (
          <Typography variant="body2" color="text.secondary" sx={{ minWidth: 'fit-content' }}>
            {totalItems} resultado{totalItems !== 1 ? 's' : ''} encontrado
            {totalItems !== 1 ? 's' : ''}
          </Typography>
        )}
      </Box>

      {loading ? (
        <Box display="flex" justifyContent="center" my={4}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Imagem</TableCell>
                  <TableCell>SKU</TableCell>
                  <TableCell>Nome</TableCell>
                  <TableCell align="right">Net Acer</TableCell>
                  <TableCell align="right" sx={{ minWidth: '180px' }}>
                    Cliente Final SP
                  </TableCell>
                  <TableCell align="center">Status</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {products && products.length > 0 ? (
                  products.map(product => (
                    <TableRow
                      key={product.id}
                      hover
                      onClick={() => navigate(`/admin/products/${product.id}`)}
                      sx={{ cursor: 'pointer' }}
                    >
                      <TableCell>
                        <Avatar
                          alt={product.name}
                          src={product.images?.find((img: any) => img.is_primary)?.image_url || ''}
                          variant="rounded"
                          sx={{ width: 60, height: 60 }}
                        />
                      </TableCell>
                      <TableCell>{product.sku || 'N/A'}</TableCell>
                      <TableCell>
                        <Typography
                          variant="body2"
                          sx={{
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            maxWidth: '200px',
                          }}
                        >
                          {product.name}
                        </Typography>
                        <Typography variant="caption" color="textSecondary">
                          {product.description?.substring(0, 50)}
                          {product.description && product.description.length > 50 ? '...' : ''}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">{formatPrice(product.price)}</TableCell>
                      <TableCell align="right" sx={{ minWidth: '180px' }}>
                        {formatPrice(product.price_with_tax)}
                      </TableCell>
                      <TableCell align="center">
                        <Chip
                          label={product.is_active ? 'Ativo' : 'Inativo'}
                          color={product.is_active ? 'success' : 'default'}
                          size="small"
                        />
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={6} align="center">
                      Nenhum produto encontrado
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            component="div"
            count={totalItems}
            page={page}
            onPageChange={handleChangePage}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            rowsPerPageOptions={[5, 10, 25, 50]}
            labelRowsPerPage="Linhas por página:"
            labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count}`}
          />
        </>
      )}
    </>
  );
};

export default AdminProductsPage;
